import { CurrencyCodes } from '@onevesthq/ov-enums';
import { Badge } from '@ovComponents/2-component/badge/badge';
import { TableCell, TableRow } from '@ovComponents/2-component/table/table';
import { formatMoneyValue } from '@utils/moneyFormatting';
import {
  Box,
  Button,
  Checkbox,
  Tooltip,
  Typography,
  useTheme,
} from 'hephaestus/web';
import { useTranslation } from 'react-i18next';
import { useLocalization } from '../../../../../hooks/useLocalization';
import type {
  ReviewTradeRow,
  ReviewTradeStatus,
} from '../../types/tradeReview.types';
import { formatAuditAccountType } from '../../utils/presentation/accountTypeLabels';
import {
  formatReviewTradeShares,
  formatReviewTradeWhy,
  resolveReviewTradeExpectedPriceCents,
  resolveTradeSettlementDays,
} from '../../utils/tradeReview/tradeReviewPresentation';

const STATUS_BADGE_COLOR: Record<
  ReviewTradeStatus,
  'positive' | 'warning' | 'neutral' | 'negative'
> = {
  READY: 'positive',
  HELD: 'warning',
  INITIATED: 'neutral',
  CANCELED: 'negative',
  REQUESTED: 'neutral',
  RECONCILED: 'positive',
};

export type ReviewTradeRowViewProps = {
  row: ReviewTradeRow;
  regionId?: string;
  selected: boolean;
  busy: boolean;
  canCancel: boolean;
  canSelect: boolean;
  onToggle: (id: string) => void;
  onCancelRow: (id: string) => void;
  localizedDate: (value: string) => string;
  showNoteColumn: boolean;
};

export const ReviewTradeRowView = ({
  row,
  regionId,
  selected,
  busy,
  canCancel,
  canSelect,
  onToggle,
  onCancelRow,
  localizedDate,
  showNoteColumn,
}: ReviewTradeRowViewProps) => {
  const { translateBackendString } = useLocalization();
  const { t } = useTranslation(['bulkRebalance']);
  const theme = useTheme();
  const ticker = row.financialProduct?.ticker || '—';
  const name = translateBackendString(row.financialProduct?.translatedName);
  const settlementDays = resolveTradeSettlementDays(row);
  const expectedPriceCents = resolveReviewTradeExpectedPriceCents(row);
  const currency = row.currency ?? CurrencyCodes.CAD;
  const showCurrency = currency !== CurrencyCodes.CAD;
  const why = formatReviewTradeWhy(t, row, localizedDate);
  const isBuy = row.type === 'BUY';

  return (
    <TableRow id={regionId} data-testid={`bulk-rebalance-review-row-${row.id}`}>
      <TableCell dense>
        {canSelect && row.isActionable ? (
          <Checkbox
            checked={selected}
            disabled={busy}
            onChange={() => onToggle(row.id)}
            slotProps={{
              input: {
                'data-testid': `bulk-rebalance-review-select-${row.id}`,
                'aria-label': t('reviewTrades.selectTradeAria', {
                  ticker,
                }),
              },
            }}
          />
        ) : null}
      </TableCell>
      <TableCell dense sx={{ pl: 3 }}>
        <Box sx={{ display: 'flex', flexDirection: 'column', minWidth: 0 }}>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 0.75,
            }}
          >
            <Typography variant='bodySmall' sx={{ fontWeight: 600 }}>
              {ticker}
            </Typography>
            {settlementDays != null && settlementDays > 0 && (
              <Badge
                label={t('reviewTrades.settlementBadge', {
                  days: settlementDays,
                })}
                color='neutral'
                small
                testId={`bulk-rebalance-review-settlement-${row.id}`}
              />
            )}
          </Box>
          {name && (
            <Typography
              variant='bodySmall'
              color='textSecondary'
              noWrap
              title={name}
            >
              {name}
            </Typography>
          )}
        </Box>
      </TableCell>
      <TableCell dense>
        <Typography
          variant='labelSmall'
          sx={{
            color: isBuy ? theme.color.positive : theme.color.negative,
            fontWeight: 600,
          }}
        >
          {t(`reviewTrades.type.${row.type}`)}
        </Typography>
      </TableCell>
      <TableCell number dense>
        {formatMoneyValue(Math.abs(row.moneyAllocatedCents))}
        {showCurrency ? (
          <Typography
            component='span'
            variant='bodySmall'
            color='textSecondary'
            sx={{ ml: 0.5 }}
          >
            {currency}
          </Typography>
        ) : null}
      </TableCell>
      <TableCell
        number
        dense
        data-testid={`bulk-rebalance-review-price-${row.id}`}
      >
        {expectedPriceCents != null
          ? formatMoneyValue(expectedPriceCents)
          : '—'}
      </TableCell>
      <TableCell number dense>
        {formatReviewTradeShares(t, row)}
      </TableCell>
      <TableCell dense>
        {formatAuditAccountType(row.subAccount?.account?.type)}
      </TableCell>
      <TableCell dense>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
          <Badge
            label={t(`reviewTrades.status.${row.status}`)}
            color={STATUS_BADGE_COLOR[row.status]}
            small
          />
          {row.isVerified && (
            <Badge
              label={t('reviewTrades.status.VERIFIED')}
              color='positive'
              small
            />
          )}
        </Box>
      </TableCell>
      {showNoteColumn && (
        <TableCell dense maxWidth='220px'>
          {why ? (
            <Tooltip title={why.tooltip ?? why.label}>
              <Typography
                variant='bodySmall'
                color='textSecondary'
                noWrap
                data-testid={`bulk-rebalance-review-note-${row.id}`}
              >
                {why.label}
              </Typography>
            </Tooltip>
          ) : null}
        </TableCell>
      )}
      <TableCell dense right>
        {canCancel && row.isActionable ? (
          <Button
            variant='text'
            size='small'
            disabled={busy}
            onClick={() => onCancelRow(row.id)}
            data-testid={`bulk-rebalance-review-cancel-${row.id}`}
          >
            {t('reviewTrades.cancelRow')}
          </Button>
        ) : null}
      </TableCell>
    </TableRow>
  );
};
